import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { projectsApi, filesApi, categoriesApi, authApi, siteSettingsApi, calendarApi, projectPlansApi } from '../services/api'
import MarkdownEditor from '../components/MarkdownEditor'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminSidebar from '../components/AdminSidebar'
import ConfirmDialog from '../components/ConfirmDialog'
import CategorySelect from '../components/CategorySelect'
import IconPicker from '../components/IconPicker'
import { Icon, DragIcon, ClipboardIcon, XIcon, CheckIcon } from '../components/Icons'
import { useToast } from '../components/ToastContainer'
import '../styles/Admin.css'

// Sortable category item component
function SortableCategoryItem({ category, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="category-item"
    >
      <div className="category-info">
        <span className="drag-handle" style={{ marginRight: '12px', color: '#999' }}>
          <DragIcon size={20} />
        </span>
        <span className="category-icon" style={{ marginRight: '12px' }}>
          <Icon name={category.icon} size={28} />
        </span>
        <div>
          <strong>{category.label}</strong>
          <div style={{ fontSize: '13px', color: '#666' }}>
            ID: {category.id} • Order: {category.order}
          </div>
          {category.description && (
            <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
              {category.description}
            </div>
          )}
        </div>
      </div>
      <div className="category-actions" onClick={(e) => e.stopPropagation()}>
        <button
          className="btn-small btn-primary"
          onClick={(e) => {
            e.stopPropagation()
            onEdit(category)
          }}
        >
          Edit
        </button>
        <button
          className="btn-small btn-danger"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(category.id)
          }}
        >
          Delete
        </button>
      </div>
    </div>
  )
}

function Admin() {
  const [projects, setProjects] = useState([])
  const [categories, setCategories] = useState([])
  const [projectPlans, setProjectPlans] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [showCategoryManager, setShowCategoryManager] = useState(false)
  const [showSiteSettings, setShowSiteSettings] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Save indicators and loading states
  const [projectSaved, setProjectSaved] = useState(false)
  const [projectSubmitting, setProjectSubmitting] = useState(false)
  const [submitType, setSubmitType] = useState(null) // 'draft' or 'published'
  const [projectMessage, setProjectMessage] = useState('')
  const [categorySaved, setCategorySaved] = useState(false)
  const [categorySubmitting, setCategorySubmitting] = useState(false)
  const [categoryMessage, setCategoryMessage] = useState('')
  const [settingsSaved, setSettingsSaved] = useState(false)
  const [settingsSubmitting, setSettingsSubmitting] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState('')
  const [deletingId, setDeletingId] = useState(null)

  // Toast notifications
  const toast = useToast()

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger'
  })

  // Apple Calendar connection testing
  const [calendarTesting, setCalendarTesting] = useState(false)
  const [calendarStatus, setCalendarStatus] = useState(null)
  const [calendarMessage, setCalendarMessage] = useState('')

  // Public calendar (ICS/webcal) connection testing
  const [publicCalendarTesting, setPublicCalendarTesting] = useState(false)
  const [publicCalendarStatus, setPublicCalendarStatus] = useState(null)
  const [publicCalendarMessage, setPublicCalendarMessage] = useState('')

  // Hosted calendar feed URL
  const [feedUrl, setFeedUrl] = useState(null)
  const [feedUrlLoading, setFeedUrlLoading] = useState(false)
  const [feedUrlCopied, setFeedUrlCopied] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'woodworking',
    content: '',
    images: [],
    files: [],
    tags: '',
    image_display_mode: 'gallery',
    related_projects: [],
    status: 'draft',
    project_plan_id: '',
  })

  const [categoryForm, setCategoryForm] = useState({
    id: '',
    label: '',
    icon: 'folder',
    description: '',
    order: 0,
  })
  const [editingCategory, setEditingCategory] = useState(null)

  const [siteSettings, setSiteSettings] = useState({
    site_title: 'KindaFearless',
    site_logo_url: null,
    site_icon_url: null,
    hero_title: 'Welcome to KindaFearless',
    hero_subtitle: 'Building, Creating, and Sharing',
    hero_image_url: null,
    hero_title_size: '3.5rem',
    hero_subtitle_size: '1.5rem',
    github_url: null,
    gitlab_url: null,
    thingiverse_url: null,
    instagram_url: null,
    apple_calendar_username: null,
    apple_calendar_password: null,
    apple_calendar_url: null,
    apple_calendar_public_url: null,
  })

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        navigate('/admin/login')
        return
      }

      try {
        const response = await authApi.verify()
        setUser(response.data.user)
        setLoading(false)
        loadProjects()
        loadCategories()
        loadSiteSettings()
        loadProjectPlans()
      } catch (error) {
        console.error('Auth verification failed:', error)
        localStorage.removeItem('auth_token')
        navigate('/admin/login')
      }
    }

    checkAuth()
  }, [navigate])

  // Handle deep linking from URL parameters
  useEffect(() => {
    const section = searchParams.get('section')
    if (section === 'categories') {
      setShowCategoryManager(true)
      setShowForm(false)
      setShowSiteSettings(false)
    } else if (section === 'settings') {
      setShowSiteSettings(true)
      setShowForm(false)
      setShowCategoryManager(false)
    } else {
      // Default view (projects)
      setShowForm(false)
      setShowCategoryManager(false)
      setShowSiteSettings(false)
    }
  }, [searchParams])

  const handleLogout = async () => {
    try {
      await authApi.logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const loadProjects = async () => {
    try {
      const response = await projectsApi.getAll({ include_drafts: true })
      setProjects(response.data)
    } catch (error) {
      console.error('Error loading projects:', error)
    }
  }

  const loadProjectPlans = async () => {
    try {
      const response = await projectPlansApi.getAll()
      setProjectPlans(response.data)
    } catch (error) {
      console.error('Error loading project plans:', error)
    }
  }

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll()
      setCategories(response.data)
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const loadSiteSettings = async () => {
    try {
      const response = await siteSettingsApi.get()
      setSiteSettings(response.data)
    } catch (error) {
      console.error('Error loading site settings:', error)
    }
  }

  const onImageDrop = async (acceptedFiles) => {
    setUploadingFiles(true)
    try {
      const uploadPromises = acceptedFiles.map((file) =>
        filesApi.upload(file, (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          console.log(`Upload progress: ${percentCompleted}%`)
        })
      )
      const responses = await Promise.all(uploadPromises)
      const imageUrls = responses.map((res) => res.data.url)
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...imageUrls],
      }))
    } catch (error) {
      console.error('Error uploading images:', error)
      toast.error('Error uploading images: ' + (error.response?.data?.detail || error.message))
    } finally {
      setUploadingFiles(false)
    }
  }

  const onFileDrop = async (acceptedFiles) => {
    setUploadingFiles(true)
    try {
      const uploadPromises = acceptedFiles.map((file) =>
        filesApi.upload(file)
      )
      const responses = await Promise.all(uploadPromises)
      const files = responses.map((res, index) => ({
        name: acceptedFiles[index].name,
        url: res.data.url,
      }))
      setFormData((prev) => ({
        ...prev,
        files: [...prev.files, ...files],
      }))
    } catch (error) {
      console.error('Error uploading files:', error)
      toast.error('Error uploading files: ' + (error.response?.data?.detail || error.message))
    } finally {
      setUploadingFiles(false)
    }
  }

  const imageDropzone = useDropzone({
    onDrop: onImageDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    multiple: true,
  })

  const fileDropzone = useDropzone({
    onDrop: onFileDrop,
    multiple: true,
  })

  const handleSubmit = async (e, statusOverride = null) => {
    e.preventDefault()
    setProjectSubmitting(true)
    setSubmitType(statusOverride || formData.status)
    setProjectMessage('')

    try {
      const projectData = {
        ...formData,
        tags: formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
        related_projects: formData.related_projects || [],
        status: statusOverride || formData.status,
      }

      if (editingProject) {
        await projectsApi.update(editingProject.id, projectData)
      } else {
        await projectsApi.create(projectData)
      }

      const action = statusOverride === 'published' ? 'published' : 'saved'
      resetForm()
      loadProjects()
      setProjectSaved(true)
      setProjectMessage(editingProject ? `Project ${action} successfully!` : `Project ${action} successfully!`)
      setTimeout(() => {
        setProjectSaved(false)
        setProjectMessage('')
      }, 3000)
    } catch (error) {
      console.error('Error saving project:', error)
      setProjectMessage('Error saving project: ' + (error.response?.data?.detail || error.message))
    } finally {
      setProjectSubmitting(false)
      setSubmitType(null)
    }
  }

  const handleEdit = (project) => {
    setEditingProject(project)
    setFormData({
      title: project.title,
      description: project.description,
      category: project.category,
      content: project.content || '',
      images: project.images || [],
      files: project.files || [],
      tags: project.tags ? project.tags.join(', ') : '',
      image_display_mode: project.image_display_mode || 'gallery',
      related_projects: project.related_projects || [],
      status: project.status || 'draft',
      project_plan_id: project.project_plan_id || '',
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Project',
      message: 'Are you sure you want to delete this project? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false })
        setDeletingId(id)
        try {
          await projectsApi.delete(id)
          loadProjects()
          toast.success('Project deleted successfully')
        } catch (error) {
          console.error('Error deleting project:', error)
          toast.error('Error deleting project: ' + (error.response?.data?.detail || error.message))
        } finally {
          setDeletingId(null)
        }
      }
    })
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'woodworking',
      content: '',
      images: [],
      files: [],
      tags: '',
      image_display_mode: 'gallery',
      related_projects: [],
      status: 'draft',
      project_plan_id: '',
    })
    setEditingProject(null)
    setShowForm(false)
  }

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }))
  }

  const removeFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }))
  }

  // Category management functions
  const handleCategorySubmit = async (e) => {
    e.preventDefault()
    setCategorySubmitting(true)
    setCategoryMessage('')

    try {
      if (editingCategory) {
        await categoriesApi.update(editingCategory.id, categoryForm)
      } else {
        await categoriesApi.create(categoryForm)
      }
      resetCategoryForm()
      loadCategories()
      setCategorySaved(true)
      setCategoryMessage(editingCategory ? 'Category updated successfully!' : 'Category created successfully!')
      setTimeout(() => {
        setCategorySaved(false)
        setCategoryMessage('')
      }, 3000)
    } catch (error) {
      console.error('Error saving category:', error)
      setCategoryMessage('Error saving category: ' + (error.response?.data?.detail || error.message))
    } finally {
      setCategorySubmitting(false)
    }
  }

  const handleEditCategory = (category) => {
    setEditingCategory(category)
    setCategoryForm({
      id: category.id,
      label: category.label,
      icon: category.icon,
      description: category.description || '',
      order: category.order || 0,
    })
  }

  const handleDeleteCategory = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category? This may affect existing projects.',
      variant: 'warning',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false })
        try {
          await categoriesApi.delete(id)
          loadCategories()
          toast.success('Category deleted successfully')
        } catch (error) {
          console.error('Error deleting category:', error)
          toast.error('Error deleting category: ' + (error.response?.data?.detail || error.message))
        }
      }
    })
  }

  const resetCategoryForm = () => {
    setCategoryForm({
      id: '',
      label: '',
      icon: 'folder',
      description: '',
      order: 0,
    })
    setEditingCategory(null)
  }

  // Handler for creating category from the project form dropdown
  const handleQuickCategoryCreate = async (categoryData) => {
    await categoriesApi.create(categoryData)
    await loadCategories()
    toast.success(`Category "${categoryData.label}" created!`)
  }

  // Drag and drop handlers for categories
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id)
      const newIndex = categories.findIndex((cat) => cat.id === over.id)

      const newCategories = arrayMove(categories, oldIndex, newIndex)

      // Update order values based on new positions
      const updatedCategories = newCategories.map((cat, index) => ({
        ...cat,
        order: index
      }))

      setCategories(updatedCategories)

      // Update each category's order in the backend
      try {
        await Promise.all(
          updatedCategories.map((cat) =>
            categoriesApi.update(cat.id, { ...cat, order: cat.order })
          )
        )
      } catch (error) {
        console.error('Error updating category order:', error)
        toast.error('Error updating category order: ' + (error.response?.data?.detail || error.message))
        loadCategories() // Reload on error
      }
    }
  }

  // Site settings handlers
  const handleSiteSettingsSubmit = async (e) => {
    e.preventDefault()
    setSettingsSubmitting(true)
    setSettingsMessage('')

    try {
      await siteSettingsApi.update(siteSettings)
      setSettingsSaved(true)
      setSettingsMessage('Settings saved successfully!')
      setTimeout(() => {
        setSettingsSaved(false)
        setSettingsMessage('')
      }, 3000)
    } catch (error) {
      console.error('Error updating site settings:', error)
      setSettingsMessage('Error updating site settings: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSettingsSubmitting(false)
    }
  }

  const handleTestCalendarConnection = async () => {
    // Check if credentials are filled in
    if (!siteSettings.apple_calendar_username || !siteSettings.apple_calendar_password || !siteSettings.apple_calendar_url) {
      setCalendarStatus('error')
      setCalendarMessage('Please fill in all Apple Calendar fields (username, password, and URL) before testing.')
      setTimeout(() => {
        setCalendarStatus(null)
        setCalendarMessage('')
      }, 5000)
      return
    }

    setCalendarTesting(true)
    setCalendarStatus(null)
    setCalendarMessage('Saving settings and testing connection...')

    try {
      // Save settings first so the backend has the latest credentials
      await siteSettingsApi.update(siteSettings)

      const response = await calendarApi.testAppleCalendarConnection()
      const result = response.data

      if (result.success) {
        setCalendarStatus('success')
        setCalendarMessage(
          `${result.message}. Found ${result.calendars_found} calendar(s): ${result.calendar_names.join(', ')}`
        )
      } else {
        setCalendarStatus('error')
        setCalendarMessage(result.message)
      }

      // Auto-hide status after 5 seconds
      setTimeout(() => {
        setCalendarStatus(null)
        setCalendarMessage('')
      }, 5000)
    } catch (error) {
      setCalendarStatus('error')
      setCalendarMessage(error.response?.data?.detail || 'Failed to test connection')

      setTimeout(() => {
        setCalendarStatus(null)
        setCalendarMessage('')
      }, 5000)
    } finally {
      setCalendarTesting(false)
    }
  }

  const handleTestPublicCalendarConnection = async () => {
    // Check if public URL is filled in
    if (!siteSettings.apple_calendar_public_url) {
      setPublicCalendarStatus('error')
      setPublicCalendarMessage('Please enter a public calendar URL (webcal:// or https:// ICS file) before testing.')
      setTimeout(() => {
        setPublicCalendarStatus(null)
        setPublicCalendarMessage('')
      }, 5000)
      return
    }

    setPublicCalendarTesting(true)
    setPublicCalendarStatus(null)
    setPublicCalendarMessage('Saving settings and testing connection...')

    try {
      // Save settings first so the backend has the latest URL
      await siteSettingsApi.update(siteSettings)

      const response = await calendarApi.testPublicCalendarConnection()
      const result = response.data

      if (result.success) {
        setPublicCalendarStatus('success')
        setPublicCalendarMessage(
          `${result.message}. Calendar: "${result.calendar_name}", ${result.events_found} events found.`
        )
      } else {
        setPublicCalendarStatus('error')
        setPublicCalendarMessage(result.message)
      }

      // Auto-hide status after 5 seconds
      setTimeout(() => {
        setPublicCalendarStatus(null)
        setPublicCalendarMessage('')
      }, 5000)
    } catch (error) {
      setPublicCalendarStatus('error')
      setPublicCalendarMessage(error.response?.data?.detail || 'Failed to test connection')

      setTimeout(() => {
        setPublicCalendarStatus(null)
        setPublicCalendarMessage('')
      }, 5000)
    } finally {
      setPublicCalendarTesting(false)
    }
  }

  const handleGetFeedUrl = async () => {
    setFeedUrlLoading(true)
    try {
      const response = await calendarApi.getFeedUrl()
      setFeedUrl(response.data)
    } catch (error) {
      console.error('Error getting feed URL:', error)
      toast.error('Failed to get feed URL')
    } finally {
      setFeedUrlLoading(false)
    }
  }

  const handleCopyFeedUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url)
      setFeedUrlCopied(true)
      toast.success('URL copied to clipboard!')
      setTimeout(() => setFeedUrlCopied(false), 2000)
    } catch (error) {
      console.error('Error copying URL:', error)
      toast.error('Failed to copy URL')
    }
  }

  const handleLogoUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return
    setUploadingFiles(true)
    try {
      const response = await filesApi.upload(acceptedFiles[0])
      setSiteSettings(prev => ({ ...prev, site_logo_url: response.data.url }))
      toast.success('Logo uploaded successfully')
    } catch (error) {
      console.error('Error uploading logo:', error)
      toast.error('Error uploading logo: ' + (error.response?.data?.detail || error.message))
    } finally {
      setUploadingFiles(false)
    }
  }

  const handleIconUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return
    setUploadingFiles(true)
    try {
      const response = await filesApi.upload(acceptedFiles[0])
      setSiteSettings(prev => ({ ...prev, site_icon_url: response.data.url }))
      toast.success('Icon uploaded successfully')
    } catch (error) {
      console.error('Error uploading icon:', error)
      toast.error('Error uploading icon: ' + (error.response?.data?.detail || error.message))
    } finally {
      setUploadingFiles(false)
    }
  }

  const handleHeroImageUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return
    setUploadingFiles(true)
    try {
      const response = await filesApi.upload(acceptedFiles[0])
      setSiteSettings(prev => ({ ...prev, hero_image_url: response.data.url }))
      toast.success('Hero image uploaded successfully')
    } catch (error) {
      console.error('Error uploading hero image:', error)
      toast.error('Error uploading hero image: ' + (error.response?.data?.detail || error.message))
    } finally {
      setUploadingFiles(false)
    }
  }

  const logoDropzone = useDropzone({
    onDrop: handleLogoUpload,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp'] },
    multiple: false,
    maxFiles: 1,
  })

  const iconDropzone = useDropzone({
    onDrop: handleIconUpload,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp', '.ico'] },
    multiple: false,
    maxFiles: 1,
  })

  const heroImageDropzone = useDropzone({
    onDrop: handleHeroImageUpload,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    multiple: false,
    maxFiles: 1,
  })

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  if (!user) {
    return null
  }

  return (
    <div className="admin-panel">
      <AdminSidebar currentPage="admin" />

      {/* Main Content */}
      <div className="admin-content">

      {showForm && (
        <div className="project-form-container">
          <h2>{editingProject ? 'Edit Project' : 'Create New Project'}</h2>
          <form onSubmit={handleSubmit} className="project-form">
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows="3"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <CategorySelect
                categories={categories}
                value={formData.category}
                onChange={(categoryId) =>
                  setFormData({ ...formData, category: categoryId })
                }
                onCategoryCreate={handleQuickCategoryCreate}
              />
            </div>

            <div className="form-group">
              <label htmlFor="project_plan_id">Linked Project Plan (Optional)</label>
              <select
                id="project_plan_id"
                value={formData.project_plan_id}
                onChange={(e) =>
                  setFormData({ ...formData, project_plan_id: e.target.value })
                }
              >
                <option value="">-- No linked plan --</option>
                {projectPlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.title} {plan.status && `(${plan.status})`}
                  </option>
                ))}
              </select>
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Link this project to an existing project plan for tracking progress
              </small>
            </div>

            <div className="form-group">
              <label>Status</label>
              <div className="status-toggle">
                <button
                  type="button"
                  className={`status-option ${formData.status === 'draft' ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, status: 'draft' })}
                >
                  Draft
                </button>
                <button
                  type="button"
                  className={`status-option ${formData.status === 'published' ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, status: 'published' })}
                >
                  Published
                </button>
              </div>
              <small style={{color: 'var(--text-light)', fontSize: '13px', marginTop: '8px', display: 'block'}}>
                Draft projects are only visible to admins. Published projects are visible to everyone.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="image_display_mode">Image Display Mode</label>
              <select
                id="image_display_mode"
                value={formData.image_display_mode}
                onChange={(e) =>
                  setFormData({ ...formData, image_display_mode: e.target.value })
                }
              >
                <option value="gallery">Gallery (separate section above content)</option>
                <option value="inline">Inline (only show images embedded in markdown)</option>
              </select>
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Gallery: Shows all uploaded images in a grid. Inline: Only shows images you reference in markdown with ![](url)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="content">Content (Markdown)</label>
              <MarkdownEditor
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                placeholder="Write your project details in Markdown..."
                height="400px"
              />
            </div>

            <div className="form-group">
              <label>Images</label>
              <div
                {...imageDropzone.getRootProps()}
                className={`dropzone ${
                  imageDropzone.isDragActive ? 'active' : ''
                }`}
              >
                <input {...imageDropzone.getInputProps()} />
                <p>
                  {uploadingFiles
                    ? 'Uploading...'
                    : 'Drag & drop images here, or click to select'}
                </p>
              </div>
              {formData.images.length > 0 && (
                <div className="uploaded-items">
                  {formData.images.map((url, index) => (
                    <div key={index} className="uploaded-item">
                      <img src={url} alt={`Upload ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`![Image description](${url})`)
                          toast.success('Markdown code copied to clipboard!')
                        }}
                        className="copy-url-btn"
                        title="Copy markdown code"
                      >
                        <ClipboardIcon size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="remove-btn"
                      >
                        <XIcon size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Files (Plans, Code, 3D Models, etc.)</label>
              <div
                {...fileDropzone.getRootProps()}
                className={`dropzone ${
                  fileDropzone.isDragActive ? 'active' : ''
                }`}
              >
                <input {...fileDropzone.getInputProps()} />
                <p>
                  {uploadingFiles
                    ? 'Uploading...'
                    : 'Drag & drop files here, or click to select'}
                </p>
              </div>
              {formData.files.length > 0 && (
                <div className="uploaded-files-list">
                  {formData.files.map((file, index) => (
                    <div key={index} className="file-item">
                      <span>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="remove-btn"
                      >
                        <XIcon size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input
                type="text"
                id="tags"
                value={formData.tags}
                onChange={(e) =>
                  setFormData({ ...formData, tags: e.target.value })
                }
                placeholder="e.g., oak, handmade, custom"
              />
            </div>

            <div className="form-group">
              <label htmlFor="related-projects">Related / Follow-on Projects</label>
              <select
                multiple
                id="related-projects"
                value={formData.related_projects}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value)
                  setFormData({ ...formData, related_projects: selected })
                }}
                style={{ minHeight: '120px' }}
              >
                {projects
                  .filter(p => p.id !== editingProject?.id)
                  .map(project => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
              </select>
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Hold Ctrl/Cmd to select multiple projects. These will appear at the bottom of the project page.
              </small>
            </div>

            <div className="form-actions">
              {projectMessage && (
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    backgroundColor: projectMessage.includes('Error') || projectMessage.includes('Error')
                      ? 'var(--danger-bg)'
                      : 'var(--success-bg)',
                    color: projectMessage.includes('Error') || projectMessage.includes('Error')
                      ? 'var(--danger-text)'
                      : 'var(--success-text)',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {projectMessage}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={projectSubmitting}
                  onClick={(e) => handleSubmit(e, 'draft')}
                >
                  {submitType === 'draft' ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={projectSubmitting}
                  onClick={(e) => handleSubmit(e, 'published')}
                >
                  {submitType === 'published' ? 'Publishing...' : (editingProject ? 'Update & Publish' : 'Publish')}
                </button>
                {projectSaved && (
                  <span style={{ color: 'var(--success-text)', display: 'inline-flex', alignItems: 'center', gap: '4px', animation: 'fadeIn 0.3s' }}>
                    <CheckIcon size={18} /> Saved
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={resetForm}
                  style={{ marginLeft: 'auto' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {showCategoryManager && (
        <div className="project-form-container">
          <h2>{editingCategory ? 'Edit Category' : 'Create New Category'}</h2>
          <form onSubmit={handleCategorySubmit} className="project-form">
            <div className="form-group">
              <label htmlFor="category-id">Category ID *</label>
              <input
                type="text"
                id="category-id"
                value={categoryForm.id}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, id: e.target.value })
                }
                placeholder="e.g., woodworking, metalworking"
                required
                disabled={editingCategory !== null}
                pattern="[a-z0-9-]+"
                title="Only lowercase letters, numbers, and hyphens"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Used in URLs and database. Cannot be changed after creation.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="category-label">Display Label *</label>
              <input
                type="text"
                id="category-label"
                value={categoryForm.label}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, label: e.target.value })
                }
                placeholder="e.g., Woodworking"
                required
              />
            </div>

            <IconPicker
              value={categoryForm.icon}
              onChange={(iconId) =>
                setCategoryForm({ ...categoryForm, icon: iconId })
              }
              label="Category Icon *"
            />

            <div className="form-group">
              <label htmlFor="category-description">Description</label>
              <textarea
                id="category-description"
                value={categoryForm.description}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, description: e.target.value })
                }
                rows="2"
                placeholder="Brief description of this category"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category-order">Display Order</label>
              <input
                type="number"
                id="category-order"
                value={categoryForm.order}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })
                }
                placeholder="0"
                min="0"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Lower numbers appear first
              </small>
            </div>

            {categoryMessage && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  backgroundColor: categoryMessage.includes('Error') || categoryMessage.includes('Failed')
                    ? 'var(--danger-bg)'
                    : 'var(--success-bg)',
                  color: categoryMessage.includes('Error') || categoryMessage.includes('Failed')
                    ? 'var(--danger-text)'
                    : 'var(--success-text)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {categoryMessage}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={categorySubmitting}>
                {categorySubmitting ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetCategoryForm}
                disabled={categorySubmitting}
              >
                Cancel
              </button>
            </div>
          </form>

          <div className="categories-list" style={{marginTop: '40px'}}>
            <h3>Existing Categories</h3>
            <p style={{color: '#666', fontSize: '14px', marginBottom: '16px'}}>
              Drag and drop to reorder categories
            </p>
            {categories.length === 0 ? (
              <p>No categories yet. Create your first category!</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={categories.map(cat => cat.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="category-items">
                    {categories.map((category) => (
                      <SortableCategoryItem
                        key={category.id}
                        category={category}
                        onEdit={handleEditCategory}
                        onDelete={handleDeleteCategory}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      )}

      {showSiteSettings && (
        <div className="project-form-container">
          <h2>Site Settings</h2>
          <form onSubmit={handleSiteSettingsSubmit} className="project-form">
            <div className="form-group">
              <label htmlFor="site-title">Site Title (Optional)</label>
              <input
                type="text"
                id="site-title"
                value={siteSettings.site_title || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, site_title: e.target.value })
                }
                placeholder="KindaFearless"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                This appears in the header and browser title. Leave empty to show only logo/icon.
              </small>
            </div>

            <div className="form-group">
              <label>Site Logo</label>
              <div
                {...logoDropzone.getRootProps()}
                className={`dropzone ${logoDropzone.isDragActive ? 'active' : ''}`}
              >
                <input {...logoDropzone.getInputProps()} />
                <p>
                  {uploadingFiles
                    ? 'Uploading...'
                    : 'Drag & drop a logo image, or click to select'}
                </p>
              </div>
              {siteSettings.site_logo_url && (
                <div className="uploaded-items" style={{marginTop: '12px'}}>
                  <div className="uploaded-item" style={{width: '200px', height: 'auto', aspectRatio: 'auto'}}>
                    <img src={siteSettings.site_logo_url} alt="Site logo" style={{objectFit: 'contain', backgroundColor: '#f3f4f6'}} />
                    <button
                      type="button"
                      onClick={() => setSiteSettings({...siteSettings, site_logo_url: null})}
                      className="remove-btn"
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                </div>
              )}
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Replaces the site title text with an image logo
              </small>
            </div>

            <div className="form-group">
              <label>Site Icon / Favicon</label>
              <div
                {...iconDropzone.getRootProps()}
                className={`dropzone ${iconDropzone.isDragActive ? 'active' : ''}`}
              >
                <input {...iconDropzone.getInputProps()} />
                <p>
                  {uploadingFiles
                    ? 'Uploading...'
                    : 'Drag & drop an icon image, or click to select'}
                </p>
              </div>
              {siteSettings.site_icon_url && (
                <div className="uploaded-items" style={{marginTop: '12px'}}>
                  <div className="uploaded-item" style={{width: '80px', height: '80px'}}>
                    <img src={siteSettings.site_icon_url} alt="Site icon" />
                    <button
                      type="button"
                      onClick={() => setSiteSettings({...siteSettings, site_icon_url: null})}
                      className="remove-btn"
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                </div>
              )}
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Shows next to the site title in the header
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="hero-title">Hero Title (Optional)</label>
              <input
                type="text"
                id="hero-title"
                value={siteSettings.hero_title || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, hero_title: e.target.value })
                }
                placeholder="Welcome to KindaFearless"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Leave empty to hide the hero title
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="hero-title-size">Hero Title Size</label>
              <select
                id="hero-title-size"
                value={siteSettings.hero_title_size || '3.5rem'}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, hero_title_size: e.target.value })
                }
              >
                <option value="2rem">Small (2rem)</option>
                <option value="2.5rem">Medium-Small (2.5rem)</option>
                <option value="3rem">Medium (3rem)</option>
                <option value="3.5rem">Large (3.5rem - Default)</option>
                <option value="4rem">Extra Large (4rem)</option>
                <option value="4.5rem">Huge (4.5rem)</option>
                <option value="5rem">Massive (5rem)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="hero-subtitle">Hero Subtitle (Optional)</label>
              <input
                type="text"
                id="hero-subtitle"
                value={siteSettings.hero_subtitle || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, hero_subtitle: e.target.value })
                }
                placeholder="Building, Creating, and Sharing"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Leave empty to hide the hero subtitle
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="hero-subtitle-size">Hero Subtitle Size</label>
              <select
                id="hero-subtitle-size"
                value={siteSettings.hero_subtitle_size || '1.5rem'}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, hero_subtitle_size: e.target.value })
                }
              >
                <option value="1rem">Small (1rem)</option>
                <option value="1.25rem">Medium-Small (1.25rem)</option>
                <option value="1.5rem">Medium (1.5rem - Default)</option>
                <option value="1.75rem">Large (1.75rem)</option>
                <option value="2rem">Extra Large (2rem)</option>
                <option value="2.5rem">Huge (2.5rem)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Hero Background Image</label>
              <div
                {...heroImageDropzone.getRootProps()}
                className={`dropzone ${heroImageDropzone.isDragActive ? 'active' : ''}`}
              >
                <input {...heroImageDropzone.getInputProps()} />
                <p>
                  {uploadingFiles
                    ? 'Uploading...'
                    : 'Drag & drop a hero background image, or click to select'}
                </p>
              </div>
              {siteSettings.hero_image_url && (
                <div className="uploaded-items" style={{marginTop: '12px'}}>
                  <div className="uploaded-item" style={{width: '100%', maxWidth: '400px', height: 'auto', aspectRatio: '16/9'}}>
                    <img src={siteSettings.hero_image_url} alt="Hero background" style={{objectFit: 'cover'}} />
                    <button
                      type="button"
                      onClick={() => setSiteSettings({...siteSettings, hero_image_url: null})}
                      className="remove-btn"
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                </div>
              )}
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Background image for the homepage hero section
              </small>
            </div>

            <h3 style={{marginTop: '32px', marginBottom: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '24px'}}>Social Links</h3>

            <div className="form-group">
              <label htmlFor="github-url">GitHub URL</label>
              <input
                type="url"
                id="github-url"
                value={siteSettings.github_url || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, github_url: e.target.value })
                }
                placeholder="https://github.com/yourusername"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Link to your GitHub profile
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="gitlab-url">GitLab URL</label>
              <input
                type="url"
                id="gitlab-url"
                value={siteSettings.gitlab_url || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, gitlab_url: e.target.value })
                }
                placeholder="https://gitlab.com/yourusername"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Link to your GitLab profile
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="thingiverse-url">Thingiverse URL</label>
              <input
                type="url"
                id="thingiverse-url"
                value={siteSettings.thingiverse_url || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, thingiverse_url: e.target.value })
                }
                placeholder="https://www.thingiverse.com/yourusername"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Link to your Thingiverse profile
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="instagram-url">Instagram URL</label>
              <input
                type="url"
                id="instagram-url"
                value={siteSettings.instagram_url || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, instagram_url: e.target.value })
                }
                placeholder="https://instagram.com/yourusername"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Link to your Instagram profile
              </small>
            </div>

            <h3 style={{ marginTop: '32px', marginBottom: '16px', color: 'var(--primary-color)' }}>
              Apple Calendar Integration
            </h3>

            <div className="form-group">
              <label htmlFor="apple-calendar-username">Apple Calendar Username</label>
              <input
                type="text"
                id="apple-calendar-username"
                value={siteSettings.apple_calendar_username || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, apple_calendar_username: e.target.value })
                }
                placeholder="your.email@icloud.com"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Your Apple ID email for calendar sync
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="apple-calendar-password">Apple Calendar App-Specific Password</label>
              <input
                type="password"
                id="apple-calendar-password"
                value={siteSettings.apple_calendar_password || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, apple_calendar_password: e.target.value })
                }
                placeholder="xxxx-xxxx-xxxx-xxxx"
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Generate an app-specific password at appleid.apple.com
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="apple-calendar-url">Apple Calendar CalDAV URL</label>
              <input
                type="url"
                id="apple-calendar-url"
                value={siteSettings.apple_calendar_url || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, apple_calendar_url: e.target.value })
                }
                placeholder="https://caldav.icloud.com/..."
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                CalDAV URL for your iCloud calendar
              </small>
            </div>

            <div style={{ marginTop: '16px', marginBottom: '24px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleTestCalendarConnection}
                disabled={calendarTesting}
              >
                {calendarTesting ? 'Testing Connection...' : 'Test Apple Calendar Connection'}
              </button>
              {calendarStatus && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    borderRadius: '6px',
                    backgroundColor: calendarStatus === 'success' ? '#d1fae5' : '#fee2e2',
                    color: calendarStatus === 'success' ? '#065f46' : '#991b1b',
                    fontSize: '14px',
                    animation: 'fadeIn 0.3s'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {calendarStatus === 'success' ? <CheckIcon size={16} /> : <XIcon size={16} />} {calendarMessage}
                  </span>
                </div>
              )}
            </div>

            <h3 style={{ marginTop: '32px', marginBottom: '16px', color: 'var(--primary-color)' }}>
              Public Calendar (Read-Only Alternative)
            </h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              If CalDAV authentication doesn't work (e.g., with Advanced Data Protection enabled), you can use a public calendar URL instead.
              This provides read-only sync. To get a public URL: In Apple Calendar, right-click a calendar → Share Calendar → Public Calendar → copy the webcal:// URL.
            </p>

            <div className="form-group">
              <label htmlFor="apple-calendar-public-url">Public Calendar URL</label>
              <input
                type="text"
                id="apple-calendar-public-url"
                value={siteSettings.apple_calendar_public_url || ''}
                onChange={(e) =>
                  setSiteSettings({ ...siteSettings, apple_calendar_public_url: e.target.value })
                }
                placeholder="webcal://p123-caldav.icloud.com/published/..."
              />
              <small style={{color: '#666', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Public calendar URL (webcal:// or https://) - Read-only, works with Advanced Data Protection
              </small>
            </div>

            <div style={{ marginTop: '16px', marginBottom: '24px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleTestPublicCalendarConnection}
                disabled={publicCalendarTesting}
              >
                {publicCalendarTesting ? 'Testing Connection...' : 'Test Public Calendar Connection'}
              </button>
              {publicCalendarStatus && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    borderRadius: '6px',
                    backgroundColor: publicCalendarStatus === 'success' ? '#d1fae5' : '#fee2e2',
                    color: publicCalendarStatus === 'success' ? '#065f46' : '#991b1b',
                    fontSize: '14px',
                    animation: 'fadeIn 0.3s'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {publicCalendarStatus === 'success' ? <CheckIcon size={16} /> : <XIcon size={16} />} {publicCalendarMessage}
                  </span>
                </div>
              )}
            </div>

            <h3 style={{ marginTop: '32px', marginBottom: '16px', color: 'var(--primary-color)' }}>
              Hosted Calendar Feed
            </h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Subscribe to your website's calendar from Apple Calendar, Google Calendar, or any app that supports ICS feeds.
              Events created from milestones and synced from external calendars will appear in this feed.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleGetFeedUrl}
                disabled={feedUrlLoading}
              >
                {feedUrlLoading ? 'Loading...' : 'Get Calendar Feed URL'}
              </button>

              {feedUrl && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '16px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--surface-bg)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                      Subscription URL (webcal://)
                    </strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="text"
                        value={feedUrl.webcal_url}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontFamily: 'monospace',
                          backgroundColor: 'var(--input-bg)'
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => handleCopyFeedUrl(feedUrl.webcal_url)}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <ClipboardIcon size={14} />
                        {feedUrlCopied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Use this URL to subscribe in calendar apps (recommended)
                    </small>
                  </div>

                  <div>
                    <strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                      Direct URL (https://)
                    </strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="text"
                        value={feedUrl.https_url}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontFamily: 'monospace',
                          backgroundColor: 'var(--input-bg)'
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleCopyFeedUrl(feedUrl.https_url)}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <ClipboardIcon size={14} />
                        Copy
                      </button>
                    </div>
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Use this URL to download the calendar file directly
                    </small>
                  </div>

                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: 'var(--info-bg)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: 'var(--info-text)'
                  }}>
                    <strong>How to subscribe:</strong>
                    <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                      <li>Apple Calendar: File → New Calendar Subscription → paste the webcal:// URL</li>
                      <li>Google Calendar: Other calendars → From URL → paste the https:// URL</li>
                      <li>Outlook: Add calendar → Subscribe from web → paste the https:// URL</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {settingsMessage && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  backgroundColor: settingsMessage.includes('Error') || settingsMessage.includes('Failed')
                    ? 'var(--danger-bg)'
                    : 'var(--success-bg)',
                  color: settingsMessage.includes('Error') || settingsMessage.includes('Failed')
                    ? 'var(--danger-text)'
                    : 'var(--success-text)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {settingsMessage}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={settingsSubmitting}>
                {settingsSubmitting ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="projects-list">
        <div className="view-header">
          <h2>Projects</h2>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            New Project
          </button>
        </div>
        {projects.length === 0 ? (
          <p>No projects yet. Create your first project!</p>
        ) : (
          <div className="admin-projects-grid">
            {projects.map((project) => (
              <div key={project.id} className="admin-project-card">
                {project.images && project.images[0] && (
                  <img src={project.images[0]} alt={project.title} />
                )}
                <div className="project-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <h3 style={{ margin: 0 }}>{project.title}</h3>
                    {(!project.status || project.status === 'draft') && (
                      <span style={{
                        backgroundColor: 'var(--warning-bg)',
                        color: 'var(--warning-text)',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        Draft
                      </span>
                    )}
                  </div>
                  <p className="category">{project.category}</p>
                  {project.project_plan_id && (
                    <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                      Linked to plan
                    </p>
                  )}
                  <div className="card-actions">
                    <button
                      className="btn-small btn-primary"
                      onClick={() => handleEdit(project)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-small btn-danger"
                      onClick={() => handleDelete(project.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
      />
    </div>
  )
}

export default Admin
